<template>
	<AppLayout :title="boardArea.name" :subtitle="trans('association.boards.areas.members.full.title')">
		<Modal id="create-full-member-modal">
			<template #title>{{ trans('admin.users.boards.areas.members.create.title') }}</template>
			<template #subtitle>{{ boardArea.name }}</template>
			<LabeledObject v-if="selectedUser" required full>
				<template #label>{{ trans('general.associate') }}</template>
				<List>
					<ListItem :title="selectedUser.full_name">
						<SoftButton :color="Color.Red" @click.stop="deselectUser">
							<i class="fa-solid fa-trash mr-2"/>
						</SoftButton>
					</ListItem>
				</List>
			</LabeledObject>
			<SearchModel v-else attribute="full_name" :label="trans('general.associate')" required relative search-type="users" @selected="selectUser"/>
			<LabeledObject required full>
				<template #label>{{ trans('association.boards.rank') }}</template>
				<select v-model="selectedRank">
					<option v-for="rank in ranks" :value="rank.id">{{ rank.name }}</option>
				</select>
			</LabeledObject>
			<LabeledObject required full>
				<template #label>{{ trans('association.boards.legal_rank') }}</template>
				<select v-model="selectedLegalRank">
					<option v-for="rank in LegalRank" :value="rank">{{ trans(`association.boards.legal_ranks.${rank}`) }}</option>
				</select>
			</LabeledObject>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="store">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
		</Modal>
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="showModal('create-full-member-modal')">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<CardGrid>
			<Card v-for="member in members" :key="`full-member-${member.id}`" :title="member.user.full_name">
				<template #badges>
					<Badge :color="Color.TrueAArEII" :active="member.active">{{ member.legal_rank ? trans(`association.boards.legal_ranks.${member.legal_rank}`) : trans('association.boards.legal_ranks.advisoryMember') }}</Badge>
				</template>
				<SanctionableDetails :sanctionable="member"/>
				<template #footer>
					<Link :as="SecondaryButton" :color="Color.Blue" :href="route('association.boards.areas.full-members.edit', {board: board.id, boardArea: boardArea.id, boardMember: member.id})">
						<i class="fa-solid fa-edit mr-2"/>
						{{ trans('general.edit') }}
					</Link>
				</template>
			</Card>
		</CardGrid>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {Link} from '@inertiajs/vue3'
import {trans} from 'laravel-vue-i18n'
import {type User} from '@/Types/User'
import {Color} from '@/Types/Enums/Color'
import {Rank} from '@/Types/Association/Rank'
import {defineProps, type Ref, ref} from 'vue'
import Card from '@/Components/UI/Cards/Card.vue'
import List from '@/Components/UI/Lists/List.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import {LegalRank} from '@/Types/Enums/LegalRank'
import ListItem from '@/Components/UI/Lists/ListItem.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {type Board} from '@/Types/Association/Boards/Board'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import SearchModel from '@/Components/Search/SearchModel.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type BoardArea} from '@/Types/Association/Boards/BoardArea'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import {type BoardMember} from '@/Types/Association/Boards/BoardMember'
import {closeModal, showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'
import SanctionableDetails from '@/Components/Association/Sanctions/SanctionableDetails.vue'

const props = defineProps<{
	board:Board,
	ranks:Array<Rank>,
	boardArea:BoardArea,
	members:Array<BoardMember>,
}>()

const board:Ref<Board> = ref(props.board)
const working:Ref<boolean> = ref(false)
const ranks:Ref<Array<Rank>> = ref(props.ranks)
const selectedUser:Ref<User|null> = ref(null)
const selectedRank:Ref<number|null> = ref(null)
const boardArea:Ref<BoardArea> = ref(props.boardArea)
const members:Ref<Array<BoardMember>> = ref(props.members)
const selectedLegalRank:Ref<LegalRank|null> = ref(null)

function selectUser(user:User):void{
	selectedUser.value = user
}

function deselectUser():void{
	selectedUser.value = null
}

function store():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('association.boards.areas.full-members.store', {board: board.value.id, boardArea: boardArea.value.id}), {
			rank_id: selectedRank.value,
			user_id: selectedUser.value.id,
			legal_rank: selectedLegalRank.value,
		}).then(({data}:{data:BoardMember}) => {
			showSuccess(trans('general.inserted'))
			members.value.unshift(data)
			closeModal('create-full-member-modal')
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>